import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FullStory, init } from '@fullstory/browser';
import { map, Observable, tap } from 'rxjs';
import { User } from '@iris-sites/interfaces/sites.interfaces';
import environment from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FullStoryService {
  private readonly _http = inject(HttpClient);
  private readonly _orgId: string = 'o-23K92V-na1';

  fullStoryEnabled = false;

  initFullStory(user: User): void {
    this.getFullStoryEnabled().subscribe((enabled) => {
      if (this.hasTrackingConsent()) {
        init({ orgId: this._orgId, devMode: !enabled });
        this.identifyUser(user);
      }
    });
  }

  toggleFullStory(enabled: boolean): void {
    this._http
      .put(`${environment.irisApi}/admin/settings/`, {
        fullstory_enabled: enabled,
      })
      .subscribe(() => {
        this.fullStoryEnabled = enabled;
      });
  }

  private identifyUser(user: User): void {
    FullStory('setIdentity', {
      uid: user.id,
      properties: {
        displayName: `${user.first_name} ${user.last_name}`,
        email: user.email,
      },
    });
  }

  private getFullStoryEnabled(): Observable<boolean> {
    return this._http
      .get<{ fullstory_enabled: boolean }>(`${environment.irisApi}/settings/`)
      .pipe(
        tap((settings) => (this.fullStoryEnabled = settings.fullstory_enabled)),
        map((settings) => settings.fullstory_enabled),
      );
  }

  /**
   * Checks if the specified group with value exists in document.cookie after URI decoding
   * @returns boolean indicating if the group exists
   */
  hasTrackingConsent(): boolean {
    if (!environment.checkForOneTrustConsent) {
      return true;
    }

    const groupToCheck = 'C0002:1';
    const cookieString = document.cookie;

    // Find the OptanonConsent cookie
    const optanonConsentMatch = cookieString.match(/OptanonConsent=([^;]*)/);

    if (!optanonConsentMatch || !optanonConsentMatch[1]) {
      return false;
    }

    const optanonConsent = optanonConsentMatch[1];

    // Find the groups parameter
    const groupsMatch = optanonConsent.match(/groups=([^&]*)/);

    if (!groupsMatch || !groupsMatch[1]) {
      return false;
    }

    // URI decode the groups value
    const decodedGroups = decodeURIComponent(groupsMatch[1]);

    // Split by comma to get individual groups
    const groupsArray = decodedGroups.split(',');

    // Check if the specified group exists
    return groupsArray.includes(groupToCheck);
  }
}
